import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import formatter from '@st-operations/uikit/lib/format.js'

import deepMerge from '_lib/deepMerge'

import LinearGradients from '../helpers/renderLinearGradients'
import { CHART_COLORS } from '../helpers/colors'
import { PROPTYPES } from '../helpers/propTypes'
import { LABEL_MAP } from '../../template/layouts/Multiline'
import { useLazyLoad } from '_hooks'

const cleanupTick = (tick) => `${tick}`.split('-')[0] || ''

const formatLegend = (data) => (value) => {
  const isEmpty = data.reduce((acc, { values }) => acc + values[value], 0) === 0
  return (
    <span
      style={{
        color: isEmpty ? '#aaaaaa' : '#3D3D3D',
        textDecoration: isEmpty ? 'line-through' : 'none',
      }}
    >
      {value}
    </span>
  )
}

const MultiLineChart = ({ data, hasLegend, colors, yLabel, config: configFromProps }) => {
  const dataSorted = [...data].sort((a, b) => a.date.localeCompare(b.date))
  const entries = Object.keys(dataSorted[0]?.values ?? {})
  const monthsUnique = [...new Set(dataSorted.map((data) => data.month))]

  const config = deepMerge(CONFIG_DEFAULT, configFromProps)
  const scaleFactor = 2

  const margins = {
    top: config.lineChart.margin.top / scaleFactor,
    right: config.lineChart.margin.right / scaleFactor,
    left: config.lineChart.margin.left + (yLabel ? 10 : 0) / scaleFactor,
    bottom: config.lineChart.margin.bottom / scaleFactor,
  }

  const [{ LineChart, XAxis, YAxis, Legend, Line }, loadingRecharts] = useLazyLoad(
    import('recharts')
  )

  return (
    <If condition={!loadingRecharts}>
      <LineChart
        width={config.lineChart.width / scaleFactor}
        height={config.lineChart.height / scaleFactor}
        data={dataSorted}
        margin={margins}
        style={{
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'top left',
          ...config.lineChart.style,
        }}
        className={classNames('ai-multi-line-chart-recharts', config.lineChart.className)}
        {...config.lineChart.extraProps}
      >
        {LinearGradients(colors, entries.length, config.lineChart.gradientsId)}
        <XAxis dataKey="month" ticks={monthsUnique} tickFormatter={cleanupTick} />
        <YAxis
          tickFormatter={
            yLabel === LABEL_MAP.Percent ? (num) => num : (num) => formatter('metric', num)
          }
          label={{
            value: yLabel,
            angle: -90,
            position: 'insideLeft',
            offset: 0,
            style: { textAnchor: 'middle', fill: '#666666', fontSize: 11, fontFamily: 'inherit' },
          }}
        />
        {hasLegend && (
          <Legend
            layout={config.legend.layout}
            align={config.legend.align}
            verticalAlign={config.legend.verticalAlign}
            iconType={config.legend.iconType}
            iconSize={config.legend.iconSize}
            wrapperStyle={{
              border: '1px solid #EBEBEB',
              width: 'fit-content',
              maxWidth: '70%',
              padding: 8,
              borderRadius: 8,
              display: 'inline-block',
              marginTop: -margins.top,
              ...config.legend.wrapperStyle,
            }}
            {...config.legend.extraProps}
            formatter={formatLegend(data)}
          />
        )}
        {entries.map((key, index) => (
          <Line
            key={key}
            dataKey={(data) => data.values[key]}
            name={key}
            type="monotone"
            stroke={`url(#${config.lineChart.gradientsId}${index})`}
            strokeWidth={2}
            legendType="circle"
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </If>
  )
}

const CONFIG_DEFAULT = {
  lineChart: {
    width: 300,
    height: 300,
    className: '',
    style: {},
    gradientsId: 'gradient-',
    extraProps: {},
    margin: {
      top: 0,
      right: 20,
      left: 0,
      bottom: 0,
    },
  },
  legend: {
    layout: 'horizontal',
    align: 'right',
    verticalAlign: 'top',
    iconType: 'circle',
    iconSize: 20,
    wrapperStyle: {},
    extraProps: {},
  },
  header: {
    title: '',
    subtitle: '',
    components: null,
  },
}

MultiLineChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      month: PropTypes.string.isRequired,
      values: PropTypes.objectOf(PropTypes.number).isRequired,
    })
  ).isRequired,
  colors: PROPTYPES.COLORS_GRADIENTS,
  hasLegend: PropTypes.bool,
  config: PropTypes.shape({
    lineChart: PropTypes.shape({
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      className: PropTypes.string,
      style: PropTypes.shape({}),
      gradientsId: PropTypes.string,
      extraProps: PropTypes.shape({}),
      margin: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
      }),
    }),
    legend: PROPTYPES.LEGEND,
    header: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      components: PropTypes.node,
    }),
  }),
  yLabel: PropTypes.string,
}

MultiLineChart.defaultProps = {
  colors: CHART_COLORS,
  hasLegend: false,
  config: CONFIG_DEFAULT,
  yLabel: null,
}

export default MultiLineChart
